import React, { useState, useEffect } from "react";
import {
  imageToImageUrl,
  createDepositRequestAPI,
  getTaxInvoiceListAPI,
  createTaxInvoiceAPI, // ✅ 추가
  updateTaxInvoiceAPI, // ✅ 추가
} from "../../../../api/index";
import { useAuth } from "../../../../hooks/useAuth";
import { useZoomSettings } from "../../../../hooks/useZoomSettings";
import "./ChargeRequest.css";

const ChargeRequest = () => {
  const { user } = useAuth();
  const { settings: zoomSettings, isReady } = useZoomSettings();
  const [charge_amount, setCharge_amount] = useState("50000");
  const [depositor_name, setDepositor_name] = useState("");
  const [tax_invoice, setTax_invoice] = useState(false);
  const [contact, setContact] = useState("");
  const [amount, setAmount] = useState("");
  const [depositBank, setDepositBank] = useState("");
  const [business_license_url, setBusiness_license_url] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 세금계산서 관련 필드들 (4개)
  const [businessLicense, setBusinessLicense] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [companyPhone, setCompanyPhone] = useState("");
  const [taxInvoiceName, setTaxInvoiceName] = useState("");

  // 세금계산서 목록 관련
  const [taxInvoiceList, setTaxInvoiceList] = useState([]);
  const [selectedTaxInvoice, setSelectedTaxInvoice] = useState("");
  const [isLoadingTaxList, setIsLoadingTaxList] = useState(false);

  // 파일 객체를 저장할 state 추가
  const [selectedFile, setSelectedFile] = useState(null);

  // 전화번호 포매팅 함수 (재사용 가능하도록 분리)
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return "";

    const value = phoneNumber.toString().replace(/[^0-9]/g, ""); // 숫자만 추출

    // 전화번호 동적 포매팅
    let formattedValue = value;

    if (value.length <= 2) {
      formattedValue = value;
    } else if (value.length <= 6) {
      formattedValue = value.replace(/(\d{2})(\d+)/, "$1-$2");
    } else if (value.length <= 9) {
      formattedValue = value.replace(/(\d{2})(\d{3})(\d+)/, "$1-$2-$3");
    } else if (value.length === 10) {
      formattedValue = value.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
    } else if (value.length === 11) {
      formattedValue = value.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
    }

    return formattedValue;
  };

  // ✅ 사용자 정보가 로드되면 연락처와 입금자명 기본값 설정
  useEffect(() => {
    if (user) {
      // 입금자명 설정 (다양한 필드명 가능성 고려)
      const userName = user.name || "";
      if (userName) {
        setDepositor_name(userName);
      } else {
      }

      // 연락처 설정
      if (user?.phone || user?.contact || user?.mobile) {
        const userPhone = user.phone || user.contact || user.mobile || "";

        // ✅ 포매팅 적용해서 설정
        const formattedPhone = formatPhoneNumber(userPhone);
        setContact(formattedPhone);
      }
    }
  }, [user]);

  useEffect(() => {
    const baseAmount = Number(charge_amount || 0);
    const finalAmount = tax_invoice ? baseAmount * 1.1 : baseAmount;

    setAmount(formatCurrency(finalAmount));

    // 세금계산서 여부에 따라 다른 계좌 정보 사용
    if (isReady && zoomSettings) {
      const bankAccount = tax_invoice
        ? zoomSettings.invoiceAccount
        : zoomSettings.depositAccount;
      setDepositBank(bankAccount || "계좌 정보 로딩 중...");
    }
  }, [charge_amount, tax_invoice, isReady, zoomSettings]);

  // 세금계산서 목록 불러오기
  useEffect(() => {
    const fetchTaxInvoiceList = async () => {
      if (!user?.id || !tax_invoice) return;

      try {
        setIsLoadingTaxList(true);
        const response = await getTaxInvoiceListAPI({
          webUserId: user.id, // ✅ 현재 사용자 ID 추가
        });

        if (response.data?.list) {
          setTaxInvoiceList(response.data.list);
        } else {
          setTaxInvoiceList([]);
        }
      } catch (error) {
        console.error("세금계산서 목록 조회 실패:", error);
        setTaxInvoiceList([]);
      } finally {
        setIsLoadingTaxList(false);
      }
    };

    fetchTaxInvoiceList();
  }, [user?.id, tax_invoice]);

  // 이전 내역 선택 핸들러
  const handleTaxInvoiceSelect = (e) => {
    const selectedId = e.target.value;
    setSelectedTaxInvoice(selectedId);

    if (selectedId) {
      const selectedData = taxInvoiceList.find(
        (item) => item._id === selectedId
      );
      if (selectedData) {
        setTaxInvoiceName(selectedData.name || "");
        setCompanyEmail(selectedData.email || "");
        setCompanyPhone(
          formatPhoneNumber(selectedData.phone?.toString() || "")
        );

        // ✅ 이미지 URL 확인 및 설정
        const imageUrl =
          selectedData.companyExtraAddress || selectedData.business_license_url;

        if (imageUrl) {
          setBusinessLicense("기존 사업자등록증");
          setSelectedFile({ isExistingUrl: true, url: imageUrl });
        } else {
          setBusinessLicense("");
          setSelectedFile(null);
        }
      } else {
        // 새로 입력하기 선택 시 모든 필드 초기화
        setTaxInvoiceName("");
        setCompanyEmail("");
        setCompanyPhone("");
        setBusinessLicense("");
        setSelectedFile(null);
      }
    } else {
      // 새로 입력하기 선택 시 모든 필드 초기화
      setTaxInvoiceName("");
      setCompanyEmail("");
      setCompanyPhone("");
      setBusinessLicense("");
      setSelectedFile(null);
    }
  };

  const formatCurrency = (value) => {
    const numeric = Number(value || 0);
    return `${numeric.toLocaleString("ko-KR")}원`; // 자동으로 정수로 변환
  };

  const isFormValid = () => {
    // 기본 필수값: web_member_id(user), amount, depositor_name, contact
    const hasUser = !!user?.id;
    const hasAmount = charge_amount && Number(charge_amount) > 0;
    const hasDepositorName = depositor_name.trim().length > 0;
    const hasContact = contact.trim().length > 0;

    // 기본 필수 항목 체크
    if (!hasUser || !hasAmount || !hasDepositorName || !hasContact) {
      return false;
    }

    // 세금계산서 발행이 체크되어 있으면 추가 필수 항목 체크 (4개)
    if (tax_invoice) {
      const hasTaxInvoiceName = taxInvoiceName.trim().length > 0;
      const hasBusinessLicense = businessLicense.trim().length > 0;
      const hasCompanyEmail = companyEmail.trim().length > 0;
      const hasCompanyPhone = companyPhone.trim().length > 0;

      return (
        hasTaxInvoiceName &&
        hasBusinessLicense &&
        hasCompanyEmail &&
        hasCompanyPhone
      );
    }

    return true;
  };

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setCharge_amount(value);
  };

  const handleAmountBlur = () => {
    const numericAmount = parseInt(charge_amount, 10); // ✅ charge_amount 사용

    if (!isNaN(numericAmount)) {
      let newAmount;

      // 1억 이상 입력불가
      if (numericAmount >= 100000000) {
        newAmount = "100000000";
      } else if (numericAmount < 50000) {
        // 5만원 미만 입력불가
        newAmount = "50000";
      } else {
        // 5만원 이상, 1억 미만이면 5만원 단위 배수로 반올림 포맷팅
        const formatted = Math.round(numericAmount / 50000) * 50000;
        newAmount = String(formatted);
      }

      setCharge_amount(newAmount);
    } else {
      // 비어있거나 숫자가 아니면 필드를 50000으로 설정
      setCharge_amount("50000");
    }
  };

  // 입금자명 변경 핸들러 (한글, 영어만 허용, / 제외)
  const handleDepositorNameChange = (e) => {
    const value = e.target.value
      .replace(/[^가-힣ㄱ-ㅎㅏ-ㅣa-zA-Z\s]/g, "")
      .replace(/\//g, "");
    setDepositor_name(value);
  };

  // 연락처 변경 핸들러 (포매팅 함수 사용)
  const handleContactChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, ""); // 숫자만 허용
    const formattedValue = formatPhoneNumber(value);

    // 최대 11자리 숫자까지만 허용
    if (value.length <= 11) {
      setContact(formattedValue);
    }
  };

  // 연락처에서 - 제거하는 함수
  const getCleanContact = (contact) => {
    return contact.replace(/-/g, "");
  };

  // 이미지 선택 핸들러 (업로드하지 않고 파일만 저장)
  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // 이미지 파일 타입 체크
    if (!file.type.startsWith("image/")) {
      alert("이미지 파일만 선택 가능합니다.");
      return;
    }

    setSelectedFile(file);
    setBusinessLicense(file.name); // 파일명만 표시용으로 저장
  };

  // 회사 대표전화 변경 핸들러 (포매팅 함수 사용, / 제외)
  const handleCompanyPhoneChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "").replace(/\//g, ""); // 숫자만 허용, / 제외
    const formattedValue = formatPhoneNumber(value);

    // 최대 11자리 숫자까지만 허용
    if (value.length <= 11) {
      setCompanyPhone(formattedValue);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid()) {
      // 어떤 필드가 비어있는지 구체적으로 알려주기
      let missingFields = [];

      if (!user?.id) missingFields.push("사용자 정보");
      if (!charge_amount || Number(charge_amount) <= 0)
        missingFields.push("충전 금액");
      if (!depositor_name.trim()) missingFields.push("입금자명");
      if (!contact.trim()) missingFields.push("입금자 연락처");

      if (tax_invoice) {
        if (!taxInvoiceName.trim()) missingFields.push("세금계산서 별칭");
        if (!businessLicense.trim()) missingFields.push("사업자등록증");
        if (!companyEmail.trim()) missingFields.push("회사 대표이메일");
        if (!companyPhone.trim()) missingFields.push("회사 대표전화");
      }

      alert(`다음 항목을 입력해주세요:\n${missingFields.join(", ")}`);
      return;
    }

    setIsSubmitting(true);

    try {
      let uploadedImageUrl = "";

      // 세금계산서 발행이 체크되어 있고 파일이 선택되었으면 업로드
      if (tax_invoice && selectedFile) {
        if (selectedFile.isExistingUrl) {
          // 기존 URL 사용
          uploadedImageUrl = selectedFile.url;
        } else {
          try {
            uploadedImageUrl = await imageToImageUrl(selectedFile);
          } catch (error) {
            console.error("❌ 이미지 업로드 에러:", error);
            throw error;
          }
        }
      }

      // ✅ finalAmount 계산 (부가세 포함 금액)
      const baseAmount = Number(charge_amount || 0);
      const finalAmount = tax_invoice
        ? Math.round(baseAmount * 1.1)
        : baseAmount;

      const requestData = {
        web_member_id: user.id,
        amount: finalAmount, // ✅ finalAmount 사용
        contact: getCleanContact(contact),
        depositor_name,
        tax_invoice,
        title: "충전 요청",
        business_license_url: null, // ✅ 초기값 설정
      };

      // 세금계산서 관련 데이터 추가
      if (tax_invoice) {
        let taxInvoiceId = null;

        // 1. 세금계산서 생성/수정 로직 (간소화)
        const existingTaxData = taxInvoiceList.find(
          (item) => item.email === companyEmail
        );

        if (existingTaxData) {
          // 업데이트
          await updateTaxInvoiceAPI(existingTaxData.id, {
            webUserId: user.id,
            name: taxInvoiceName,
            phone: getCleanContact(companyPhone),
            email: companyEmail,
            businessRegistrationNumber: parseInt(getCleanContact(companyPhone)), // ✅ 회사 대표전화를 사업자등록번호로 사용
            companyExtraAddress: uploadedImageUrl,
          });

          taxInvoiceId = existingTaxData.id; // 기존 ID 사용
        } else {
          // 생성
          const createResponse = await createTaxInvoiceAPI({
            webUserId: user.id,
            name: taxInvoiceName,
            phone: getCleanContact(companyPhone),
            email: companyEmail,
            businessRegistrationNumber: parseInt(getCleanContact(companyPhone)), // ✅ 회사 대표전화를 사업자등록번호로 사용
            companyExtraAddress: uploadedImageUrl,
          });
          taxInvoiceId = createResponse.data.data.id; // ✅ 올바른 경로로 수정
        }

        // ✅ 세금계산서 ID를 business_license_url에 넣기 (이미지 URL이 아닌!)
        requestData.business_license_url = taxInvoiceId;
      }

      // 충전 요청 API 호출
      await createDepositRequestAPI(requestData);

      alert(
        `충전 신청이 완료되었습니다.\n${depositBank}으로 ${amount} 입금 바랍니다.`
      );
    } catch (error) {
      console.error("❌ 충전 요청 실패:", error);
      const errorMessage =
        error.response?.data?.msg ||
        error.message ||
        "충전 요청에 실패했습니다.";
      alert(`충전 요청 실패: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="charge-request-form" onSubmit={handleSubmit}>
      <div className="form-row">
        <label className="form-field">
          <span className="label">충전 금액</span>
          <input
            type="text"
            className="input"
            value={charge_amount}
            onChange={handleAmountChange}
            onBlur={handleAmountBlur}
            placeholder="충전할 금액을 입력하세요"
          />
        </label>
        <label className="form-field">
          <span className="label">입금자명</span>
          <input
            className="input"
            placeholder="홍길동"
            value={depositor_name}
            onChange={handleDepositorNameChange} // ✅ 새로운 핸들러 사용
          />
        </label>
        <label className="form-field">
          <span className="label">입금자 연락처</span>
          <input
            type="tel"
            className="input"
            placeholder="010-1234-5678"
            value={contact}
            onChange={handleContactChange} // ✅ 새로운 핸들러 사용
            maxLength="13" // ✅ 최대 길이 제한
          />
        </label>
      </div>
      <div className="form-row checkbox-row">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={tax_invoice}
            onChange={(e) => setTax_invoice(e.target.checked)}
          />
          <span>세금계산서 발행</span>
        </label>
      </div>
      {tax_invoice && (
        <div className="tax-invoice-section">
          {/* 1층: 이전 내역 불러오기 */}
          <div className="form-row">
            <label className="form-field">
              <span className="label">이전 내역 불러오기</span>
              <select
                className="input"
                value={selectedTaxInvoice}
                onChange={handleTaxInvoiceSelect}
                disabled={isLoadingTaxList}
              >
                <option value="">새로 입력하기</option>
                {taxInvoiceList.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {/* 2층: 세금계산서 별칭, 사업자등록증, 회사 대표이메일, 회사 대표전화 (4개) */}
          <div className="form-row">
            <label className="form-field">
              <span className="label">세금계산서 별칭</span>
              <input
                type="text"
                className="input"
                placeholder="예: 우리회사 세금계산서"
                value={taxInvoiceName}
                onChange={(e) => setTaxInvoiceName(e.target.value)}
              />
            </label>
            <label className="form-field">
              <span className="label">회사 대표이메일</span>
              <input
                type="email"
                className="input"
                placeholder="example@company.com"
                value={companyEmail}
                onChange={(e) => setCompanyEmail(e.target.value)}
              />
            </label>
            <label className="form-field">
              <span className="label">회사 대표전화</span>
              <input
                type="tel"
                className="input"
                placeholder="02-1234-5678"
                value={companyPhone}
                onChange={handleCompanyPhoneChange} // ✅ 새로운 핸들러 사용
                maxLength="13" // ✅ 최대 길이 제한
              />
            </label>
            {/* 사업자등록증 업로드 */}
            <label className="form-field">
              <span className="label">사업자등록증</span>
              <div className="file-upload-container">
                <input
                  type="text"
                  className="input"
                  placeholder="이미지를 선택해주세요"
                  value={businessLicense}
                  readOnly
                />
                <input
                  type="file"
                  id="business-license-upload"
                  accept="image/*"
                  onChange={handleImageSelect} // ✅ 변경된 핸들러
                  style={{ display: "none" }}
                />
                <button
                  type="button"
                  className="image-select-button"
                  onClick={() =>
                    document.getElementById("business-license-upload").click()
                  }
                  disabled={isSubmitting} // ✅ 업로드 중이 아닌 제출 중일 때 비활성화
                >
                  {selectedFile ? "이미지 선택됨" : "이미지 선택"}
                </button>
              </div>
            </label>
          </div>
        </div>
      )}

      {/* 다음에도 사용할게요 체크박스
      {tax_invoice && (
        <div className="form-row save-checkbox-row">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={saveForNext}
              onChange={(e) => setSaveForNext(e.target.checked)}
            />
            <span>다음에도 사용할게요</span>
          </label>
        </div>
      )} */}

      <div className="payment-info">
        <div className="info-box">
          <span className="label">
            {tax_invoice ? "결제 금액(부가세 포함)" : "결제 금액"}
          </span>
          <div className="value-box">{amount}</div>
        </div>
        <div className="info-box">
          <span className="label">입금 은행</span>
          <div className="value-box">{depositBank}</div>
        </div>
      </div>

      <div className="actions">
        <button
          type="submit"
          className="submit-button"
          disabled={!isFormValid() || isSubmitting} // ✅ 필수값 검증 + 제출 중 비활성화
        >
          {isSubmitting ? "요청 중..." : "충전 요청"}
        </button>
      </div>
    </form>
  );
};

export default ChargeRequest;
